import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Radio,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Backdrop,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { updateMoveService } from "../../redux/action/move-service";
import Header from "../header/Header";
import { ErrorOutline } from "@mui/icons-material";

const ApartmentInfo = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [floorNumber, setFloorNumber] = useState("");
  const { canonicalId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [floorOptions, setFloorOptions] = useState({
    isElevator: false,
    isStairs: false,
    not_applicable: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const navigate = useNavigate();
  const { businessName, carrierCanonicalId } = useParams();
  const lastStep = 3;
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);
  const handleOptionChange = (option: string) => {
    setFloorOptions({
      isElevator: option === "elevator",
      isStairs: option === "stairs",
      not_applicable: option === "notApplicable" ? "Not Applicable" : "",
    });
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Processing your information...");

      setTimeout(async () => {
        try {
          const moveServiceData = {
            canonical_id: canonicalId,
            floor_number: floorNumber,
            ...floorOptions,
            last_step: lastStep,
          };

          const res = await dispatch<any>(updateMoveService(moveServiceData));
          unwrapResult(res);
          navigate(
            `/${businessName}/${carrierCanonicalId}/delivery-details/${canonicalId}`
          );
        } catch (err: any) {
          setError(err?.message || "Failed to update move service");
        } finally {
          setIsLoading(false);
        }
      }, 2000);
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <div>
      <Header />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container
        sx={{
          width: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: isMobile ? "20px" : "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "16px" : "18px"}
            textAlign="center"
            mb={2}
          >
            Your Apartment Information
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          <Box>
            <Typography fontWeight={400} fontSize={16} mb={2} textAlign="left">
              What floor is your apartment?
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter floor number"
              value={floorNumber}
              onChange={(e) => {
                const newFloorNumber = e.target.value;
                if (/^\d{0,2}$/.test(newFloorNumber)) {
                  if (parseInt(newFloorNumber) > 20) {
                    setError("Floor number cannot be greater than 20");
                  } else {
                    setError(null);
                    setFloorNumber(newFloorNumber);
                  }
                }
              }}
              sx={{ mb: 2 }}
            />
            {error && (
              <Box
                sx={{
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  padding: "15px",
                  border: "1px solid #f5c6cb",
                  borderRadius: "5px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  maxWidth: "400px",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <ErrorOutline sx={{ fontSize: "20px", paddingRight: "10px" }} />{" "}
                <Box>{error}</Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />
          <Typography fontSize={16} textAlign="left" mb={3}>
            We need to know the following information to make it easy to
            navigate, reduce space, and be user-friendly.
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 2 }} />

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography fontSize={16}>Elevator</Typography>
              <Radio
                checked={floorOptions.isElevator}
                onChange={() => handleOptionChange("elevator")}
                sx={{ color: theme.palette.primary.main }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography fontSize={16}>Stairs</Typography>
              <Radio
                checked={floorOptions.isStairs}
                onChange={() => handleOptionChange("stairs")}
                sx={{ color: theme.palette.primary.main }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography fontSize={16}>Not Applicable</Typography>
              <Radio
                checked={!!floorOptions.not_applicable}
                onChange={() => handleOptionChange("notApplicable")}
                sx={{ color: theme.palette.primary.main }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
            marginTop: "20px",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
            }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
              "&:disabled": { opacity: 0.7 },
            }}
            onClick={handleContinue}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Continue"}
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default ApartmentInfo;
